import axios from "axios";

class AxiosService {
  constructor() {
    if (!AxiosService.instance) {
      this.axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        timeout: 100000,
      });

      // Add the interceptor here
      this.axiosInstance.interceptors.response.use(
        (response) => {
          // On success, add ok: true and problem: null
          return { ...response, ok: true, problem: null };
        },
        (error) => {
          let problem = null;
          if (error.code === "ERR_NETWORK") {
            problem = "NETWORK_ERROR";
            // All of the below errors are considered.
            // No internet connection
            // DNS lookup failure
            // Connection refused or timed out
            // CORS errors (in browsers)
            // Server is unreachable
            // SSL handshake failure
          } else if (error.response) {
            const status = error.response.status;
            if (status >= 400 && status < 500) {
              problem = "CLIENT_ERROR";
              // All of the below error codes are considered.
              // 400 – Bad Request
              // 401 – Unauthorized
              // 402 – Payment Required
              // 403 – Forbidden
              // 404 – Not Found
              // 405 – Method Not Allowed
              // 406 – Not Acceptable
              // 407 – Proxy Authentication Required
              // 408 – Request Timeout
              // 409 – Conflict
              // 410 – Gone
              // 411 – Length Required
              // 412 – Precondition Failed
              // 413 – Payload Too Large
              // 414 – URI Too Long
              // 415 – Unsupported Media Type
              // 416 – Range Not Satisfiable
              // 417 – Expectation Failed
              // 418 – I’m a teapot
              // 422 – Unprocessable Entity
              // 426 – Upgrade Required
              // 429 – Too Many Requests
              // 451 – Unavailable For Legal Reasons
            } else if (status >= 500) {
              problem = "SERVER_ERROR";
              // All of the below error codes are considered.
              // 500 – Internal Server Error
              // 501 – Not Implemented
              // 502 – Bad Gateway
              // 503 – Service Unavailable
              // 504 – Gateway Timeout
              // 505 – HTTP Version Not Supported
              // 506 – Variant Also Negotiates
              // 507 – Insufficient Storage
              // 508 – Loop Detected
              // 510 – Not Extended
              // 511 – Network Authentication Required
            }
          } else {
            problem = "CLIENT_ERROR";
          }
          return Promise.resolve({
            ok: false,
            problem,
            status: error.response?.status,
            data: error.response?.data,
            originalError: error,
          });
        },
      );

      AxiosService.instance = this;
    }

    return AxiosService.instance;
  }

  _buildHeaders(customHeaders) {
    const userToken =
      window.sessionStorage.getItem("token") ||
      window.localStorage.getItem("token");
    const defaultHeaders = { "content-type": "application/json" };
    if (userToken) {
      return {
        Authorization: `Bearer ${userToken}`,
        ...defaultHeaders,
        ...customHeaders,
      };
    } else {
      const path = window.location.href;
      if (!path.includes("get-in-touch-form")) window.location = "/";
    }
  }

  async get(url, headers = {}) {
    return this.axiosInstance.get(url, {
      headers: this._buildHeaders(headers),
    });
  }

  async post(url, payload, headers = {}) {
    return this.axiosInstance.post(url, payload, {
      headers: this._buildHeaders(headers),
    });
  }

  async put(url, payload, headers = {}) {
    return this.axiosInstance.put(url, payload, {
      headers: this._buildHeaders(headers),
    });
  }

  async patch(url, payload, headers = {}) {
    return this.axiosInstance.patch(url, payload, {
      headers: this._buildHeaders(headers),
    });
  }
}

const api = new AxiosService();
Object.freeze(api);

export default api;
